import { useState } from 'react';
import { Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation, LANGUAGES, Language } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';

interface LanguageSelectorProps {
  variant?: 'default' | 'ghost' | 'minimal';
  showLabel?: boolean;
  className?: string;
}

export function LanguageSelector({ 
  variant = 'ghost', 
  showLabel = false,
  className 
}: LanguageSelectorProps) {
  const { currentLanguage, setLanguage, isTranslating } = useTranslation();
  const [open, setOpen] = useState(false);
  
  const currentLang = LANGUAGES.find(l => l.code === currentLanguage) || LANGUAGES[0];

  const handleSelect = (lang: Language) => {
    setLanguage(lang);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === 'minimal' ? 'ghost' : variant}
          size="sm"
          className={cn(
            "gap-1.5 min-h-[40px] px-2.5",
            variant === 'minimal' && "p-2",
            isTranslating && "animate-pulse",
            className
          )}
          aria-label="Select language"
        >
          <span className="text-base">{currentLang.flag}</span>
          {showLabel && (
            <span className="hidden sm:inline text-sm">{currentLang.name}</span>
          )}
          {!showLabel && variant !== 'minimal' && (
            <Globe className="h-4 w-4 opacity-60" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-48 bg-white border border-slate-200 shadow-lg"
      >
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 cursor-pointer",
              lang.dir === 'rtl' && "flex-row-reverse text-right",
              currentLanguage === lang.code && "bg-blue-50 text-blue-600"
            )}
          >
            <span className="text-lg">{lang.flag}</span>
            <span className="flex-1">{lang.name}</span>
            {currentLanguage === lang.code && (
              <span className="text-blue-600">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
